import { Box, Chip, Popover, Alert } from "@mui/material";
import { createUseStyles } from "$utils/createUseStyles";
import { useState } from "react";
import { theme } from "$utils/theme/bataillog";

import { Button, Text } from "$components";
import { Order, Reception, Suborder } from "$types";
import JOrderTree from "$components/JOrderTree/JOrderTree";
import RefTypeAdvice from "../RefTypeAdvice";
import SplitBehaviourRadioGroup from "./SplitBehaviourRadioGroup";

export enum SplitBehaviour {
  GROUPED = "GROUPED",
  SPLITTED = "SPLITTED",
}

type Props = {
  isReadOnly: boolean;
  onSelectedSuborders: (newSuborders: Suborder[]) => void;
  reception: Reception;
  selectableOrders: Order[];
};

function SubordersSelect({
  isReadOnly,
  onSelectedSuborders,
  reception,
  selectableOrders,
}: Props) {
  const orders = reception?.orders || [];
  const classes = useStyles({
    nbOfOrders: selectableOrders.length,
    nbOfSuborders: selectableOrders.reduce(
      (total, o) => total + o.suborders.length,
      0
    ),
  });
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [splitBehaviour, setSplitBehaviour] = useState<SplitBehaviour>(
    SplitBehaviour.GROUPED
  );
  const popoverOpen = Boolean(popoverAnchor);
  const [tmpSelectedSuborders, setTmpSelectedSuborders] = useState<string[]>(
    []
  );

  const handleOnCheck = (
    checkedItems: (Order & { hasOnlyOneSuborder: boolean })[]
  ) => {
    const checkedSuborders = checkedItems.map((item) => item.suborders[0]);
    setTmpSelectedSuborders(
      checkedSuborders.map((suborder) => suborder.reference)
    );
  };

  const handleUnselectSuborder = (index: number) => {
    setTmpSelectedSuborders(
      tmpSelectedSuborders.filter((suborder, i) => i !== index)
    );
  };

  const handleSelectSuborders = () => {
    // Populate orders & suborders
    const newSuborders = tmpSelectedSuborders.map((suborderRef) => {
      const order = orders.find((o) =>
        o.suborders.find((sub) => sub.reference === suborderRef)
      );
      return {
        order,
        ...order.suborders.find((sub) => sub.reference === suborderRef),
      } as Suborder;
    });

    switch (splitBehaviour) {
      case SplitBehaviour.SPLITTED:
        // Call onSelectedSuborders once for each suborder individually
        newSuborders.forEach((newSuborder) => {
          onSelectedSuborders([newSuborder]); // Each call has one suborder
        });
        break;
      case SplitBehaviour.GROUPED:
        // Call onSelectedSuborders once with all suborders
        onSelectedSuborders(newSuborders); // Single call with all suborders
        break;
      default:
        console.error(`Unknown SplitBehaviour: ${splitBehaviour}`);
    }
    setTmpSelectedSuborders([]);
  };

  return (
    <>
      {!isReadOnly && selectableOrders?.length > 0 && (
        <div className={classes.advices} key={`advice-${reception.id}`}>
          {selectableOrders?.map((order) => (
            <RefTypeAdvice
              key={order.reference}
              reception={reception}
              order={order}
              selectedSuborders={tmpSelectedSuborders}
            />
          ))}
        </div>
      )}
      {selectableOrders?.length === 0 ? (
        <Alert severity="success">
          Aucune sous-commande à référencer (seules les sous-commandes dont au
          moins un produit a été reçu peuvent être référencées).
        </Alert>
      ) : (
        <Box className={classes.ordersSelectBlock}>
          <Box>
            <Button
              disabled={isReadOnly}
              variant="outlined"
              onClick={(event) => setPopoverAnchor(event.currentTarget)}
            >
              Sélectionner une ou plusieurs (sous-)commande(s)
            </Button>
            <Popover
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              anchorEl={popoverAnchor}
              onClose={() => setPopoverAnchor(null)}
              open={popoverOpen}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <div className={classes.popoverContent}>
                {selectableOrders?.length > 0 ? (
                  <>
                    <JOrderTree
                      key={selectableOrders.map((o) => o.reference).join(",")}
                      allowOrderWithoutChildrenCheck
                      checkedOrders={tmpSelectedSuborders.map((suborderRef) => {
                        const order = orders.find((o) =>
                          o.suborders.some((s) => s.reference === suborderRef)
                        );
                        return {
                          ...order,
                          suborders: order.suborders.filter(
                            (s) => s.reference === suborderRef
                          ),
                        };
                      })}
                      displayOrderWithoutProducts
                      expandAllNodes
                      hideProducts
                      onCheck={handleOnCheck}
                      orders={selectableOrders}
                      showCheckBox
                    />
                    <SplitBehaviourRadioGroup
                      setSplitBehaviour={setSplitBehaviour}
                      splitBehaviour={splitBehaviour}
                    />
                  </>
                ) : (
                  <Text>Aucune sous-commande disponible.</Text>
                )}
              </div>
            </Popover>

            <div className={classes.chipWrapper}>
              {tmpSelectedSuborders.map((suborderRef, index) => {
                const fromOrder = orders.find((order) =>
                  order?.suborders.some(
                    (suborder) => suborder.reference === suborderRef
                  )
                );
                const renderedReference =
                  fromOrder.suborders?.length === 1
                    ? fromOrder.reference
                    : suborderRef;
                return (
                  <Chip
                    key={suborderRef}
                    label={renderedReference}
                    onDelete={() => handleUnselectSuborder(index)}
                    className={classes.suborderChip}
                  />
                );
              })}
            </div>
          </Box>

          <Button
            disabled={tmpSelectedSuborders.length === 0}
            onClick={handleSelectSuborders}
            className={classes.referenceButton}
          >
            Référencer
          </Button>

          <Text>
            Seules les sous-commandes dont au moins un produit a été reçu
            peuvent être référencées.
          </Text>
        </Box>
      )}
    </>
  );
}

const useStyles = createUseStyles(() => ({
  advices: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(1)}px`,
  },
  chipWrapper: {
    display: "flex",
    gap: `${theme.spacing(1)}px`,
  },
  wrapper: {
    maxWidth: "1200px",
  },
  ordersSelectBlock: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: (props: { nbOfOrders: number; nbOfSuborders: number }) =>
      // The dropdown menu is displayed at the bottom of the button, in order to not hide the advices displayed above.
      // This margin is used to make some room below the button to avoid the dropdown menu to be on top of the advices.
      `${theme.spacing(
        Math.max(props.nbOfOrders * 6 + props.nbOfSuborders * 6, 1)
      )}px`,
  },
  referenceButton: {
    minWidth: "80px !important",
    height: "fit-content",
  },
  popoverContent: {
    padding: `${theme.spacing(2)}px`,
  },
  suborderChip: {
    fontSize: "14px !important",
    height: "24px !important",
    marginTop: `${theme.spacing(1)}px`,
  },
}));

export default SubordersSelect;
